/**
 * Multipart upload and authenticated viewing of bank-slip evidence.
 *
 * The shared client is JSON-only, and the API keeps slips in a private store
 * (never a web root), so both directions need the bearer token by hand.
 */
import { ApiError } from "@evocha/api-client";
import { API_BASE_URL, DEALER_STORAGE_KEY } from "./api";

function bearer(): string | null {
  try {
    const raw = localStorage.getItem(DEALER_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    return typeof stored?.access_token === "string" ? stored.access_token : null;
  } catch {
    return null;
  }
}

function problem(response: Response, detail: string): ApiError {
  return new ApiError({
    type: "about:blank",
    title: response.statusText || "Upload failed",
    status: response.status,
    code: "upload_failed",
    detail,
  });
}

/** A short text error the dealer can act on, taken from the API problem. */
function detailFrom(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    for (const key of ["detail", "message", "title"]) {
      if (typeof record[key] === "string" && record[key]) return record[key] as string;
    }
  }
  return fallback;
}

export async function submitSlip<T>(
  path: string,
  fields: Record<string, string | undefined>,
  file: File,
): Promise<T> {
  const token = bearer();
  if (!token) throw problem(new Response(null, { status: 401 }), "Your session expired. Sign in again.");

  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== "") form.append(key, value);
  }
  form.append("slip", file, file.name);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw problem(response, detailFrom(body, "We could not save that slip. Try again."));
  }
  return body as T;
}

export async function fetchEvidenceUrl(path: string): Promise<string> {
  const token = bearer();
  if (!token) throw problem(new Response(null, { status: 401 }), "Your session expired. Sign in again.");
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw problem(response, "This document is no longer available.");
  }
  const type = response.headers.get("content-type") ?? "application/octet-stream";
  const blob = await response.blob();
  return URL.createObjectURL(new Blob([blob], { type }));
}

export function releaseEvidenceUrl(url: string | null): void {
  if (url) URL.revokeObjectURL(url);
}
